/**
 * GA4 Platform Configuration (admin)
 * Read-only view of the GA4 measurement IDs configured via backend
 * environment variables, plus a setup guide and quick links.
 */

import React, { useState, useEffect } from 'react';
import {
  Store, ShieldCheck, Copy, Check, AlertTriangle, Info,
  Activity, Zap, BookOpen, ArrowUpRight, BarChart3,
} from 'lucide-react';
import './GA4PlatformConfig.css';

function GA4PlatformConfig() {
  const [config, setConfig] = useState({
    platform: { measurementId: '', enabled: false },
    merchantApp: { measurementId: '', enabled: false },
    adminApp: { measurementId: '', enabled: false },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/ga4/admin/measurement-ids', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch platform GA4 configuration');
      setConfig(await res.json());
      setError(null);
    } catch (e) {
      console.error('GA4 config fetch failed:', e);
      setError('Failed to load GA4 configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (key, value) => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    });
  };

  const STREAMS = [
    {
      key: 'platform',
      icon: <Activity size={18} />,
      title: 'Platform analytics',
      desc: 'Internal operations and system-wide metrics.',
      envVar: 'GA4_PLATFORM_MEASUREMENT_ID',
    },
    {
      key: 'merchantApp',
      icon: <Store size={18} />,
      title: 'Merchant dashboard',
      desc: 'Feature usage and merchant behavior on the dashboard.',
      envVar: 'GA4_MERCHANT_APP_MEASUREMENT_ID',
    },
    {
      key: 'adminApp',
      icon: <ShieldCheck size={18} />,
      title: 'Admin dashboard',
      desc: 'Admin operations and platform administration activity.',
      envVar: 'GA4_ADMIN_APP_MEASUREMENT_ID',
    },
  ];

  const LINKS = [
    { url: 'https://analytics.google.com', label: 'GA4 dashboard', icon: <BarChart3 size={16} /> },
    { url: 'https://analytics.google.com/analytics/web/#/p{propertyId}/reports/explorer', label: 'Exploration reports', icon: <Activity size={16} /> },
    { url: 'https://analytics.google.com/analytics/web/#/p{propertyId}/reports/realtime', label: 'Realtime reports', icon: <Zap size={16} /> },
    { url: 'https://developers.google.com/analytics/devguides/collection/protocol/ga4', label: 'GA4 documentation', icon: <BookOpen size={16} /> },
  ];

  if (loading) {
    return (
      <div className="ga-page">
        <div className="ga-loading">Loading GA4 configuration…</div>
      </div>
    );
  }

  return (
    <div className="ga-page">
      <header className="ga-header">
        <h1 className="ga-title">Platform analytics</h1>
        <p className="ga-subtitle">
          Google Analytics 4 measurement IDs for each platform app. Configured via backend
          environment variables — read-only here.
        </p>
      </header>

      {error && (
        <div className="ga-banner ga-banner-error">
          <AlertTriangle size={16} />
          <span>{error}</span>
          <button
            type="button"
            className="ga-banner-close"
            onClick={() => setError(null)}
            aria-label="Dismiss"
          >×</button>
        </div>
      )}

      {/* Measurement-ID cards */}
      <section className="ga-grid">
        {STREAMS.map(({ key, icon, title, desc, envVar }) => {
          const stream = config[key] || {};
          const isCopied = copiedKey === key;
          return (
            <article key={key} className={`ga-card${stream.enabled ? ' is-enabled' : ''}`}>
              <div className="ga-card-head">
                <span className="ga-card-icon">{icon}</span>
                <h2 className="ga-card-title">{title}</h2>
                <span className={`ga-pill ${stream.enabled ? 'ga-pill-on' : 'ga-pill-off'}`}>
                  {stream.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <p className="ga-card-desc">{desc}</p>
              {stream.measurementId ? (
                <div className="ga-id-row">
                  <label className="ga-id-label">Measurement ID</label>
                  <div className="ga-id-value">
                    <code>{stream.measurementId}</code>
                    <button
                      type="button"
                      className="ga-copy"
                      onClick={() => handleCopy(key, stream.measurementId)}
                      aria-label={isCopied ? 'Copied' : 'Copy measurement ID'}
                      title={isCopied ? 'Copied' : 'Copy'}
                    >
                      {isCopied ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ga-empty">
                  Not configured — set <code>{envVar}</code> in the backend <code>.env</code>.
                </div>
              )}
            </article>
          );
        })}
      </section>

      {/* Quick links */}
      <section className="ga-card">
        <div className="ga-card-head">
          <h2 className="ga-card-title">Quick links</h2>
        </div>
        <div className="ga-links">
          {LINKS.map(({ url, label, icon }) => (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="ga-link"
            >
              <span className="ga-link-icon">{icon}</span>
              <span className="ga-link-label">{label}</span>
              <ArrowUpRight size={14} className="ga-link-arrow" />
            </a>
          ))}
        </div>
      </section>

      {/* Setup guide */}
      <section className="ga-card">
        <div className="ga-card-head">
          <h2 className="ga-card-title">Setup instructions</h2>
        </div>
        <ol className="ga-steps">
          <li>Create a GA4 property in Google Analytics for each app.</li>
          <li>Open <strong>Admin → Data Streams</strong> in each property and copy the Measurement ID.</li>
          <li>Add the IDs to the backend <code>.env</code>:</li>
        </ol>
        <pre className="ga-env">{`# Platform-wide GA4
GA4_PLATFORM_ENABLED=true
GA4_PLATFORM_MEASUREMENT_ID=G-XXXXXXXXXX
GA4_PLATFORM_API_SECRET=your_api_secret

# Merchant App
GA4_MERCHANT_APP_ENABLED=true
GA4_MERCHANT_APP_MEASUREMENT_ID=G-YYYYYYYYYY

# Admin App
GA4_ADMIN_APP_ENABLED=true
GA4_ADMIN_APP_MEASUREMENT_ID=G-ZZZZZZZZZZ`}</pre>
        <ol className="ga-steps" start="4">
          <li>Restart the backend server.</li>
          <li>Verify tracking via GA4 DebugView.</li>
        </ol>
      </section>

      {/* Merchant storefront note */}
      <div className="ga-note">
        <Info size={16} className="ga-note-icon" />
        <p>
          Merchants configure their own GA4 properties for their storefronts under
          <strong> Settings → Integrations → GA4</strong> in the merchant dashboard.
        </p>
      </div>
    </div>
  );
}

export default GA4PlatformConfig;
